import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../home/components/Footer';
import { createNewsUrl } from '../../../utils/urlUtils';
import ShareToMessageModal, { SharedContent } from '../../../components/base/ShareToMessageModal';
import { useAuth } from '../../../hooks/useAuth';
import { supabase } from '../../../lib/supabase';
import RelatedCreators from './components/RelatedCreators';

const newsCategories = [
  { id: 'all', name: 'All News', icon: 'ri-newspaper-line', color: 'text-gray-700', bgColor: 'bg-gray-100' },
  { id: 'production', name: 'Production', icon: 'ri-film-line', color: 'text-amber-600', bgColor: 'bg-amber-100' },
  { id: 'streaming', name: 'Streaming', icon: 'ri-live-line', color: 'text-orange-600', bgColor: 'bg-orange-100' },
  { id: 'awards', name: 'Awards', icon: 'ri-award-line', color: 'text-yellow-600', bgColor: 'bg-yellow-100' },
  { id: 'casting', name: 'Casting', icon: 'ri-user-star-line', color: 'text-rose-600', bgColor: 'bg-rose-100' },
  { id: 'technology', name: 'Technology', icon: 'ri-cpu-line', color: 'text-teal-600', bgColor: 'bg-teal-100' },
  { id: 'industry', name: 'Industry', icon: 'ri-building-line', color: 'text-indigo-600', bgColor: 'bg-indigo-100' },
];

interface NewsArticle {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  category: string;
  image: string;
  author_name: string;
  author_avatar: string;
  published_at: string;
  read_time: number;
  is_breaking: boolean;
  is_featured: boolean;
  views: number;
  tags: string[];
  ai_summary: any;
  status: string;
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { 
    weekday: 'long',
    month: 'long', 
    day: 'numeric',
    year: 'numeric'
  });
};

const getCategoryInfo = (categoryId: string) => {
  return newsCategories.find(c => c.id === categoryId) || newsCategories[0];
};

export default function NewsDetailPage() {
  const { urlParam } = useParams<{ urlParam: string }>();
  const { user } = useAuth();
  const [article, setArticle] = useState<NewsArticle | null>(null);
  const [relatedArticles, setRelatedArticles] = useState<NewsArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [shareToMessageOpen, setShareToMessageOpen] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [summaryExpanded, setSummaryExpanded] = useState(true);
  const [summaryGenerated, setSummaryGenerated] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);

  // Fetch article from database
  useEffect(() => {
    const fetchArticle = async () => {
      if (!urlParam) return;
      try {
        setLoading(true);
        
        // Look up article by slug directly
        const { data, error } = await supabase
          .from('news_articles')
          .select('*')
          .eq('status', 'published')
          .eq('slug', urlParam)
          .maybeSingle();

        if (error) throw error;
        if (data) {
          setArticle(data);
          // Increment views
          await supabase
            .from('news_articles')
            .update({ views: (data.views || 0) + 1 })
            .eq('id', data.id);
          // Fetch related articles
          fetchRelated(data);
        }
      } catch (err) {
        console.error('Error fetching article:', err);
      } finally {
        setLoading(false);
      }
    };

    const fetchRelated = async (current: NewsArticle) => {
      try {
        const { data } = await supabase
          .from('news_articles')
          .select('*')
          .eq('status', 'published')
          .neq('id', current.id)
          .order('published_at', { ascending: false })
          .limit(20);

        if (data) {
          // Score and sort by relevance
          const scored = data.map(a => {
            let score = 0;
            if (a.category === current.category) score += 30;
            const matchingTags = (a.tags || []).filter((t: string) => (current.tags || []).includes(t));
            score += matchingTags.length * 15;
            const daysDiff = Math.abs(new Date(current.published_at).getTime() - new Date(a.published_at).getTime()) / (1000 * 60 * 60 * 24);
            if (daysDiff <= 7) score += 10;
            if ((a.views || 0) > 10000) score += 5;
            if (a.is_featured) score += 3;
            return { article: a, score, matchingTags, sameCategory: a.category === current.category };
          })
          .filter(item => item.score > 0)
          .sort((a, b) => b.score - a.score)
          .slice(0, 4);

          setRelatedArticles(scored.map(s => ({ ...s.article, _matchingTags: s.matchingTags, _sameCategory: s.sameCategory })));
        }
      } catch (err) {
        console.error('Error fetching related:', err);
      }
    };

    fetchArticle();
  }, [urlParam]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <i className="ri-loader-4-line animate-spin text-5xl text-amber-500 mb-4"></i>
          <h2 className="text-lg font-semibold text-gray-900">Loading article...</h2>
        </div>
        <Footer />
      </div>
    );
  }
  
  if (!article) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <i className="ri-newspaper-line text-6xl text-gray-300 mb-4"></i>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Article Not Found</h1>
          <p className="text-gray-600 mb-6">The article you&apos;re looking for doesn&apos;t exist or has been removed.</p>
          <Link 
            to="/news" 
            className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 text-white rounded-lg font-medium hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            Back to News
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const categoryInfo = getCategoryInfo(article.category);

  // Share to message content
  const shareContent: SharedContent = {
    type: 'post',
    id: article.id.toString(),
    title: article.title,
    description: article.excerpt,
    image: article.image,
    url: `${window.location.origin}${createNewsUrl(article.id, article.title)}`,
    meta: {
      category: categoryInfo.name,
      categoryColor: categoryInfo.color.includes('amber') ? '#f59e0b' : 
                     categoryInfo.color.includes('rose') ? '#f43f5e' :
                     categoryInfo.color.includes('emerald') ? '#10b981' :
                     categoryInfo.color.includes('sky') ? '#0ea5e9' :
                     categoryInfo.color.includes('violet') ? '#8b5cf6' : '#6b7280',
      author: article.author_name
    }
  };

  const handleSendToConversation = async (conversationId: string, content: SharedContent) => {
    if (!user) return;
    
    const messageContent = JSON.stringify({
      type: 'shared_content',
      contentType: 'news',
      ...content
    });

    await supabase.from('messages').insert({
      conversation_id: conversationId,
      sender_id: user.id,
      content: messageContent,
      message_type: 'shared_content'
    });

    await supabase
      .from('conversations')
      .update({ updated_at: new Date().toISOString() })
      .eq('id', conversationId);
  };

  const handleStartNewConversation = async (userId: string, content: SharedContent) => {
    if (!user) return;

    const { data: existingParticipants } = await supabase
      .from('conversation_participants')
      .select('conversation_id')
      .eq('user_id', user.id);

    if (existingParticipants && existingParticipants.length > 0) {
      const convIds = existingParticipants.map(p => p.conversation_id);
      const { data: otherParticipants } = await supabase
        .from('conversation_participants')
        .select('conversation_id')
        .eq('user_id', userId)
        .in('conversation_id', convIds);

      if (otherParticipants && otherParticipants.length > 0) {
        await handleSendToConversation(otherParticipants[0].conversation_id, content);
        return;
      }
    }

    const { data: newConv } = await supabase
      .from('conversations')
      .insert({ is_group: false })
      .select()
      .single();

    if (newConv) {
      await supabase.from('conversation_participants').insert([
        { conversation_id: newConv.id, user_id: user.id },
        { conversation_id: newConv.id, user_id: userId }
      ]);

      await handleSendToConversation(newConv.id, content);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareContent.url);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleShareTwitter = () => {
    const text = `Check out this article: "${article.title}"`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareContent.url)}`, '_blank');
  };

  const handleShareFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareContent.url)}`, '_blank');
  };

  const handleShareWhatsApp = () => {
    const text = `Check out this article: "${article.title}" ${shareContent.url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const handleGenerateSummary = () => {
    setSummaryLoading(true);
    setTimeout(() => {
      setSummaryLoading(false);
      setSummaryGenerated(true);
    }, 1500);
  };

  // Check if content is HTML
  const isHtmlContent = article.content && (article.content.includes('<p') || article.content.includes('<h') || article.content.includes('<ul') || article.content.includes('<ol') || article.content.includes('<blockquote'));

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Article Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Back Button + Breadcrumb */}
          <div className="flex items-center gap-4 mb-6">
            <Link
              to="/news"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-amber-600 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Back to News
            </Link>
            <nav className="flex items-center gap-2 text-sm text-gray-500">
              <Link to="/" className="hover:text-amber-600 cursor-pointer">Home</Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to="/news" className="hover:text-amber-600 cursor-pointer">News</Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to={`/news?category=${article.category}`} className="hover:text-amber-600 cursor-pointer">
                {categoryInfo.name}
              </Link>
            </nav>
          </div>
          
          {/* Category & Badges */}
          <div className="flex items-center gap-3 mb-4">
            <span className={`px-3 py-1 ${categoryInfo.bgColor} ${categoryInfo.color} text-sm font-medium rounded-full`}>
              {categoryInfo.name}
            </span>
            {article.is_breaking && (
              <span className="px-3 py-1 bg-red-500 text-white text-sm font-medium rounded-full flex items-center gap-1">
                <i className="ri-alarm-warning-line"></i>
                Breaking News
              </span>
            )}
            {article.is_featured && (
              <span className="px-3 py-1 bg-amber-500 text-white text-sm font-medium rounded-full">
                Featured
              </span>
            )}
          </div>
          
          {/* Title */}
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4 leading-tight">
            {article.title}
          </h1>
          
          {/* Excerpt */}
          <p className="text-xl text-gray-600 mb-6">
            {article.excerpt}
          </p>
          
          {/* Meta Info */}
          <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500">
            <div className="flex items-center gap-2">
              {article.author_avatar && (
                <img 
                  src={article.author_avatar} 
                  alt={article.author_name}
                  className="w-10 h-10 rounded-full object-cover"
                />
              )}
              <div>
                <p className="font-medium text-gray-900">{article.author_name}</p>
                <p className="text-xs">Author</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <i className="ri-calendar-line"></i>
              {formatDate(article.published_at)}
            </div>
            <div className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              {article.read_time} min read
            </div>
            <div className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {(article.views || 0).toLocaleString()} views
            </div>
          </div>
        </div>
      </div>

      {/* Featured Image */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-4">
        <img 
          src={article.image} 
          alt={article.title}
          className="w-full h-64 sm:h-96 object-cover object-top rounded-xl shadow-lg"
        />
      </div>

      {/* AI Summary Section */}
      {article.ai_summary && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200 overflow-hidden">
            {!summaryGenerated ? (
              <div className="px-5 py-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                      <i className="ri-sparkling-2-fill text-white text-lg"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                        AI Summary
                        <span className="px-2 py-0.5 bg-amber-200 text-amber-800 text-xs font-medium rounded-full">
                          Beta
                        </span>
                      </h3>
                      <p className="text-xs text-gray-500">Get a quick overview of key points</p>
                    </div>
                  </div>
                  <button
                    onClick={handleGenerateSummary}
                    disabled={summaryLoading}
                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg font-medium hover:from-amber-600 hover:to-orange-600 transition-all cursor-pointer whitespace-nowrap disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {summaryLoading ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        <span>Generating...</span>
                      </>
                    ) : (
                      <>
                        <i className="ri-sparkling-line"></i>
                        <span>Summarize Article</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <button
                  onClick={() => setSummaryExpanded(!summaryExpanded)}
                  className="w-full flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-amber-100/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                      <i className="ri-sparkling-2-fill text-white text-lg"></i>
                    </div>
                    <div className="text-left">
                      <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                        AI Summary
                        <span className="px-2 py-0.5 bg-amber-200 text-amber-800 text-xs font-medium rounded-full">
                          Beta
                        </span>
                      </h3>
                      <p className="text-xs text-gray-500">Quick overview of key points</p>
                    </div>
                  </div>
                  <i className={`ri-arrow-${summaryExpanded ? 'up' : 'down'}-s-line text-gray-500 text-xl transition-transform`}></i>
                </button>
                
                {summaryExpanded && (
                  <div className="px-5 pb-5 pt-0">
                    <div className="space-y-2.5">
                      {article.ai_summary.keyPoints?.map((point: string, index: number) => (
                        <div key={index} className="flex items-start gap-3">
                          <div className="w-5 h-5 rounded-full bg-amber-500 text-white text-xs font-medium flex items-center justify-center flex-shrink-0 mt-0.5">
                            {index + 1}
                          </div>
                          <p className="text-gray-700 text-sm leading-relaxed">{point}</p>
                        </div>
                      ))}
                    </div>
                    
                    <div className="flex items-center gap-4 mt-4 pt-4 border-t border-amber-200">
                      <div className="flex items-center gap-1.5 text-xs text-gray-500">
                        <i className="ri-time-line"></i>
                        <span>{article.ai_summary.readingTime} read</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-xs">
                        <i className={`ri-emotion-${article.ai_summary.sentiment === 'positive' ? 'happy' : article.ai_summary.sentiment === 'negative' ? 'unhappy' : 'normal'}-line ${article.ai_summary.sentiment === 'positive' ? 'text-emerald-500' : article.ai_summary.sentiment === 'negative' ? 'text-red-500' : 'text-gray-500'}`}></i>
                        <span className={`capitalize ${article.ai_summary.sentiment === 'positive' ? 'text-emerald-600' : article.ai_summary.sentiment === 'negative' ? 'text-red-600' : 'text-gray-500'}`}>
                          {article.ai_summary.sentiment} tone
                        </span>
                      </div>
                      <div className="ml-auto flex items-center gap-2">
                        <span className="text-xs text-gray-400">Was this helpful?</span>
                        <button className="w-7 h-7 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-500 hover:text-emerald-500 hover:border-emerald-300 transition-colors cursor-pointer">
                          <i className="ri-thumb-up-line text-sm"></i>
                        </button>
                        <button className="w-7 h-7 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-500 hover:text-red-500 hover:border-red-300 transition-colors cursor-pointer">
                          <i className="ri-thumb-down-line text-sm"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Article Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="bg-white rounded-xl border border-gray-200 p-6 sm:p-10">
          {/* Article Body - Render HTML or plain text */}
          {isHtmlContent ? (
            <div 
              className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-p:leading-relaxed prose-a:text-amber-600 prose-a:hover:text-amber-700 prose-blockquote:border-amber-400 prose-blockquote:text-gray-600 prose-strong:text-gray-900 prose-ul:text-gray-700 prose-ol:text-gray-700 prose-li:text-gray-700 prose-img:rounded-lg prose-img:shadow-md"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          ) : (
            <div className="prose prose-lg max-w-none">
              {article.content?.split('\n\n').map((paragraph, idx) => (
                <p key={idx} className="text-gray-700 leading-relaxed mb-6">
                  {paragraph}
                </p>
              ))}
            </div>
          )}
          
          {/* Tags */}
          {article.tags && article.tags.length > 0 && (
            <div className="mt-8 pt-6 border-t border-gray-200">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Tags</h4>
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/news?search=${tag}`}
                    className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full hover:bg-amber-100 hover:text-amber-700 transition-colors cursor-pointer"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            </div>
          )}
          
          {/* Share */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Share this article</h4>
            <div className="flex items-center gap-3 flex-wrap">
              {user && (
                <button 
                  onClick={() => setShareToMessageOpen(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-pink-500 to-amber-500 text-white rounded-full hover:shadow-lg transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-message-3-line"></i>
                  <span className="text-sm font-medium">Send to Chat</span>
                </button>
              )}
              <button 
                onClick={handleShareTwitter}
                className="w-10 h-10 flex items-center justify-center bg-black text-white rounded-full hover:bg-gray-800 transition-colors cursor-pointer"
              >
                <i className="ri-twitter-x-line"></i>
              </button>
              <button 
                onClick={handleShareFacebook}
                className="w-10 h-10 flex items-center justify-center bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors cursor-pointer"
              >
                <i className="ri-facebook-fill"></i>
              </button>
              <button 
                onClick={handleShareWhatsApp}
                className="w-10 h-10 flex items-center justify-center bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors cursor-pointer"
              >
                <i className="ri-whatsapp-line"></i>
              </button>
              <button 
                onClick={handleCopyLink}
                className={`w-10 h-10 flex items-center justify-center rounded-full transition-colors cursor-pointer ${
                  copiedLink 
                    ? 'bg-emerald-500 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                <i className={copiedLink ? 'ri-check-line' : 'ri-link'}></i>
              </button>
              {copiedLink && (
                <span className="text-sm text-emerald-600 font-medium">Link copied!</span>
              )}
            </div>
          </div>
        </div>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <div className="mt-10">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Related Articles</h3>
                <p className="text-sm text-gray-500 mt-1">Based on similar topics and tags</p>
              </div>
              <Link 
                to={`/news?category=${article.category}`}
                className="text-sm text-amber-600 hover:text-amber-700 font-medium flex items-center gap-1 cursor-pointer"
              >
                More in {categoryInfo.name}
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {relatedArticles.map((related: any) => {
                const relatedCategoryInfo = getCategoryInfo(related.category);
                const matchingTags = related._matchingTags || [];
                const sameCategory = related._sameCategory || false;
                return (
                  <Link 
                    key={related.id} 
                    to={createNewsUrl(related.id, related.title)}
                    className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg hover:border-amber-200 transition-all group"
                  >
                    <div className="relative">
                      <img 
                        src={related.image} 
                        alt={related.title}
                        className="w-full h-36 object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                      <span className={`absolute top-2 left-2 px-2 py-0.5 ${relatedCategoryInfo.bgColor} ${relatedCategoryInfo.color} text-xs font-medium rounded-full`}>
                        {relatedCategoryInfo.name}
                      </span>
                      {sameCategory && (
                        <span className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center bg-amber-500 text-white text-xs rounded-full" title="Same category">
                          <i className="ri-links-line text-sm"></i>
                        </span>
                      )}
                    </div>
                    <div className="p-4">
                      <h4 className="font-semibold text-gray-900 text-sm line-clamp-2 group-hover:text-amber-600 transition-colors leading-snug">
                        {related.title}
                      </h4>
                      
                      {matchingTags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {matchingTags.slice(0, 2).map((tag: string) => (
                            <span 
                              key={tag} 
                              className="px-2 py-0.5 bg-amber-50 text-amber-700 text-xs rounded-full border border-amber-200"
                            >
                              #{tag}
                            </span>
                          ))}
                          {matchingTags.length > 2 && (
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded-full">
                              +{matchingTags.length - 2}
                            </span>
                          )}
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between mt-3 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <i className="ri-time-line"></i>
                          {related.read_time} min
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i>
                          {(related.views || 0) >= 1000 ? `${((related.views || 0) / 1000).toFixed(1)}k` : related.views}
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
            
            <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-100">
              <div className="flex items-start gap-2">
                <i className="ri-lightbulb-line text-amber-600 mt-0.5"></i>
                <div className="text-sm">
                  <span className="text-amber-800 font-medium">Why these articles?</span>
                  <p className="text-amber-700 mt-0.5">
                    Recommended based on shared tags
                    {relatedArticles.some((r: any) => r._sameCategory) && ', same category'}
                    , and publication timing.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {relatedArticles.length < 2 && (
          <div className="mt-8 p-6 bg-white rounded-xl border border-gray-200">
            <h4 className="font-semibold text-gray-900 mb-3">Explore More {categoryInfo.name} News</h4>
            <p className="text-sm text-gray-600 mb-4">Discover more articles in this category</p>
            <Link 
              to={`/news?category=${article.category}`}
              className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Browse {categoryInfo.name}
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        )}

        {/* Related Creators - Compact */}
        <RelatedCreators
          articleTags={article.tags || []}
          articleCategory={article.category}
          articleTitle={article.title}
        />

        {/* Back to News */}
        <div className="mt-10 text-center">
          <Link 
            to="/news" 
            className="inline-flex items-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            Back to News Archive
          </Link>
        </div>
      </div>

      <Footer />

      {/* Share to Message Modal */}
      <ShareToMessageModal
        isOpen={shareToMessageOpen}
        onClose={() => setShareToMessageOpen(false)}
        content={shareContent}
        onSend={handleSendToConversation}
        onStartNewConversation={handleStartNewConversation}
      />
    </div>
  );
}
